import React from 'react';
import { Info, Clock, Calendar, Globe, History } from 'lucide-react';

/**
 * Компонент с информацией и интересными фактами о часовых поясах
 */
const TimezoneFacts = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Всё о часовых поясах: история, особенности, интересные факты
      </h2>
      
      <div className="prose prose-indigo dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-indigo-500" />
          Что такое часовые пояса?
        </h3>
        <p>
          <strong>Часовой пояс</strong> — это регион на Земле, в котором действует единое стандартное время. В теории, Земля разделена на 24 часовых пояса, каждый шириной примерно 15° долготы (поскольку Земля поворачивается на 15° каждый час). Однако на практике границы часовых поясов следуют политическим и административным границам, что делает их контуры неровными.
        </p>
        <p>
          Каждый часовой пояс определяется своим смещением от всемирного координированного времени (UTC). Например, московское время — UTC+3, что означает, что в Москве время на 3 часа опережает UTC.
        </p>

        <h3 className="flex items-center gap-2">
          <History className="w-5 h-5 text-orange-500" />
          История часовых поясов
        </h3>
        <p>
          До XIX века единое стандартное время не было необходимостью, поскольку коммуникации и путешествия были медленными. Каждый город использовал местное солнечное время, основанное на положении солнца.
        </p>
        <p>
          Первые часовые пояса были введены железнодорожными компаниями США и Канады в 1883 году для стандартизации расписаний. В 1884 году на Международной меридианной конференции Гринвичский меридиан был принят в качестве нулевого меридиана (0° долготы), и мир был разделен на 24 часовых пояса.
        </p>
        <p>
          В России первое поясное время было введено декретом Совета Народных Комиссаров от 8 февраля 1919 года. До этого на территории Российской Империи действовало местное время разных городов.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium">История часовых поясов в России</h4>
          <p className="text-blue-600 dark:text-blue-400 text-sm mb-0">
            С 1917 по 2014 год Россия неоднократно меняла систему часовых поясов и правила перехода на летнее/зимнее время. В 2014 году страна окончательно перешла на постоянное зимнее время, отказавшись от сезонного перевода часов.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Globe className="w-5 h-5 text-green-500" />
          Особенности часовых поясов России
        </h3>
        <p>
          Россия — крупнейшая по территории страна мира, которая протянулась через 11 часовых поясов от Калининградской области (UTC+2) до Чукотки и Камчатки (UTC+12). Это создает уникальную ситуацию: когда жители Камчатки начинают рабочий день, в Калининграде уже вечер.
        </p>
        <p>
          Современная система часовых поясов в России:
        </p>
        <ul>
          <li><strong>UTC+2 (MSK-1)</strong>: Калининградская область</li>
          <li><strong>UTC+3 (MSK)</strong>: Москва, центральная Россия, Санкт-Петербург, Ростов-на-Дону, Краснодар</li>
          <li><strong>UTC+4 (MSK+1)</strong>: Самара, Удмуртия, Астрахань</li>
          <li><strong>UTC+5 (MSK+2)</strong>: Екатеринбург, Пермь, Уфа, Челябинск, Оренбург</li>
          <li><strong>UTC+6 (MSK+3)</strong>: Омск</li>
          <li><strong>UTC+7 (MSK+4)</strong>: Красноярск, Новосибирск, Кемерово, Томск</li>
          <li><strong>UTC+8 (MSK+5)</strong>: Иркутск, Улан-Удэ</li>
          <li><strong>UTC+9 (MSK+6)</strong>: Якутск, Чита</li>
          <li><strong>UTC+10 (MSK+7)</strong>: Владивосток, Хабаровск</li>
          <li><strong>UTC+11 (MSK+8)</strong>: Магадан, Южно-Сахалинск</li>
          <li><strong>UTC+12 (MSK+9)</strong>: Анадырь, Петропавловск-Камчатский</li>
        </ul>

        <h3>Интересные факты о часовых поясах</h3>
        <ul>
          <li>Самая большая разница во времени между двумя точками на Земле составляет 26 часов (между островами Лайн в Кирибати, UTC+14, и островами Бейкер и Хауленд, UTC-12)</li>
          <li>Франция вместе со всеми заморскими территориями имеет 12 часовых поясов — больше, чем у любой другой страны</li>
          <li>Китай, несмотря на размеры сопоставимые с США, официально использует только один часовой пояс (UTC+8)</li>
          <li>Непал — единственная страна с часовым смещением UTC+5:45</li>
          <li>Самым первым регионом, встречающим Новый год, является Кирибати (острова Лайн, UTC+14)</li>
          <li>В России Новый год отмечают 11 раз — для каждого часового пояса</li>
        </ul>

        <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg border border-yellow-100 dark:border-yellow-800 my-6">
          <h4 className="text-yellow-700 dark:text-yellow-300 font-medium flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            Международная линия перемены дат
          </h4>
          <p className="text-yellow-600 dark:text-yellow-400 text-sm mb-0">
            Это воображаемая линия на поверхности Земли, проходящая примерно по 180° меридиану. При пересечении этой линии с запада на восток календарная дата уменьшается на одни сутки, а с востока на запад — увеличивается. Линия специально проведена так, чтобы не пересекать сушу и обходить архипелаги.
          </p>
        </div>

        <h3>Переход на летнее и зимнее время</h3>
        <p>
          Многие страны мира практикуют переход на летнее время (DST), когда весной часы переводятся на 1 час вперед, а осенью — на 1 час назад. Это делается для более эффективного использования светлого времени суток.
        </p>
        <p>
          В России с 2014 года отменен сезонный перевод часов, и страна использует постоянное "зимнее" время. До этого переход на летнее/зимнее время практиковался с 1981 года.
        </p>
        <p>
          В большинстве европейских стран, а также в некоторых регионах США и Канады, переход на летнее время осуществляется в последнее воскресенье марта, а на зимнее — в последнее воскресенье октября.
        </p>

        <h3>Как учитывать часовые пояса при планировании</h3>
        <ul>
          <li>При планировании международных звонков и видеоконференций обязательно учитывайте разницу во времени</li>
          <li>Используйте инструменты для конвертации времени, чтобы найти подходящее время для всех участников</li>
          <li>При путешествиях через несколько часовых поясов учитывайте джетлаг (нарушение биоритмов)</li>
          <li>Для бизнес-мероприятий лучше выбирать время, которое будет рабочим для всех сторон</li>
          <li>При планировании телефонных звонков в Россию из других стран, помните о большой разнице между часовыми поясами в стране</li>
        </ul>

        <p className="font-semibold mt-6">
          Наш сервис "Мировые часы" поможет вам легко отслеживать время в разных городах мира, конвертировать время между часовыми поясами и планировать международные встречи с учетом часовых поясов всех участников!
        </p>
      </div>
    </article>
  );
};

export default TimezoneFacts;